/**
 * @typedef {Object} PlanDetails
 * @property {string} code
 * @property {number} monthlyCost
 * @property {number} [annualCost]
 * @property {number} setupFee
 * @property {string} customerType
 * @property {string} planName
 * @property {string} capacityLabel
 * @property {number} capacityAmount
 * @property {string} [secondaryCapacityLabel]
 * @property {number} [secondaryCapacityAmount]
 */

export const CUSTOMER_TYPES = {
  LENDER: 'Lender',
  REAL_ESTATE_AGENT: 'Real Estate Agent',
  INSURANCE_AGENT: 'Insurance Agent',
  TITLE_AGENT: 'Title Agent',
};

export const PLAN_NAMES = {
  STARTER: 'Starter',
  INDIVIDUAL: 'Individual',
  PARTNER: 'Partner',
  TEAMS: 'Teams',
  FREEMIUM_STARTER: 'Freemium Starter',
  PRO: 'Pro'
};

/** @type {Object.<string, PlanDetails>} */
const planData = {
  'hb-lender-starter': {
    code: 'hb-lender-starter',
    monthlyCost: 125,
    annualCost: 1250,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.STARTER,
    capacityLabel: 'Homeowners',
    capacityAmount: 100,
    secondaryCapacityLabel: 'buyers',
    secondaryCapacityAmount: 100
  },
  'hb-lender-pro': {
    code: 'hb-lender-pro',
    monthlyCost: 225,
    annualCost: 2250,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.INDIVIDUAL,
    capacityLabel: 'Homeowners',
    capacityAmount: 500,
    secondaryCapacityLabel: 'buyers',
    secondaryCapacityAmount: 1500
  },
  'hb-agent-solo': {
    code: 'hb-agent-solo',
    monthlyCost: 50,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.STARTER,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'hb-agent-partner': {
    code: 'hb-agent-partner',
    monthlyCost: 25,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.PARTNER,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'hb-agent-team': {
    code: 'hb-agent-team',
    monthlyCost: 100,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.TEAMS,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'freemium-starter': {
    code: 'freemium-starter',
    monthlyCost: 0,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.FREEMIUM_STARTER,
    capacityLabel: 'homebuyers',
    capacityAmount: 25
  },
  'hb-insurance-agent-pro': {
    code: 'hb-insurance-agent-pro',
    monthlyCost: 200,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.INSURANCE_AGENT,
    planName: PLAN_NAMES.PRO,
    capacityLabel: 'contacts',
    capacityAmount: 2000
  },
  'hb-title-agent-pro': {
    code: 'hb-title-agent-pro',
    monthlyCost: 200,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.TITLE_AGENT,
    planName: PLAN_NAMES.PRO,
    capacityLabel: 'contacts',
    capacityAmount: 500
  },
  'fairway-individual': {
    code: 'fairway-individual',
    monthlyCost: 180,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Fairway Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Active Portal Users'
    ]
  },
  'fairway-individual-starter': {
    code: 'fairway-individual-starter',
    monthlyCost: 135,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Fairway Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      'Unlimited Clients & Email Engagement',
      '100 Active Portal Users'
    ]
  }
};

/**
 * Mapping of corporate profile IDs to plan codes
 * Can be a single plan code (string) or multiple plan codes (array)
 * @type {Object.<string, string|string[]>}
 */
export const companyIdToPlanMap = {
  // '00810f49-666e-4294-8a8c-9a9feec5c622': ['fairway-individual', 'fairway-individual-starter'], // swap to local company id for testing
  '218d90e3-3f41-4000-b6ae-467edfc4e929': ['fairway-individual', 'fairway-individual-starter']
};

/**
 * @param {string} planCode
 * @returns {PlanDetails|null}
 */
export const getPlanDetails = (planCode) => {
  return planData[planCode] || null;
};

/**
 * Get plan code(s) for a given company ID
 * @param {string} companyId
 * @returns {string[]|null} - Always returns an array of plan codes or null
 */
export const getPlanCodesForCompany = (companyId) => {
  const plans = companyIdToPlanMap[companyId];
  if (!plans) return null;
  // Always return as array for consistency
  return Array.isArray(plans) ? plans : [plans];
};

export const plans = Object.values(planData);
